<template>
  <div class="ru-status-summary-table" data-testid="ru-status-summary-table">
    <v-simple-table dense class="status-table" data-testid="ru-status-summary-table__table">
      <template v-slot:default>
        <thead class="tableHeaderBg">
          <tr data-testid="ru-status-summary-table__header-row">
            <th class="text-left" data-testid="ru-status-summary-table__header-scope">
              {{ scopeHeaderLabel }}
            </th>
            <th class="text-left" data-testid="ru-status-summary-table__header-comparison">
              Comparison
            </th>
            <th 
              v-for="column in tableColumns" 
              :key="column.id"
              class="text-center"
              :class="column.headerClasses"
              :data-testid="`ru-status-summary-table__header-${column.id}`"
            >
              <span class="column-header">{{ column.shortLabel }}</span>
            </th>
          </tr>
        </thead>
        <tbody v-if="loading">
          <!-- Skeleton Local Rows -->
          <tr 
            v-for="(comparison, index) in normalizedComparisons" 
            :key="`skel-local-${comparison.key}`"
          >
            <td 
              v-if="index === 0" 
              :rowspan="normalizedComparisons.length"
              class="scope-cell"
            >
               <v-skeleton-loader type="text" width="60" class="mx-auto"></v-skeleton-loader>
            </td>
            <td class="comparison-cell">
               <v-skeleton-loader type="text" width="100"></v-skeleton-loader>
            </td>
            <td 
              v-for="column in tableColumns" 
              :key="column.id"
              class="text-center count-cell"
            >
                <div class="d-flex justify-center">
                   <v-skeleton-loader type="text" width="30"></v-skeleton-loader>
                </div>
            </td>
          </tr>
          
          <!-- Skeleton Regional Rows -->
          <tr 
            v-for="(comparison, index) in normalizedComparisons" 
            :key="`skel-regional-${comparison.key}`"
          >
            <td 
              v-if="index === 0" 
              :rowspan="normalizedComparisons.length"
              class="scope-cell"
            >
               <v-skeleton-loader type="text" width="60" class="mx-auto"></v-skeleton-loader>
            </td>
            <td class="comparison-cell">
               <v-skeleton-loader type="text" width="100"></v-skeleton-loader>
            </td>
            <td 
              v-for="column in tableColumns" 
              :key="column.id"
              class="text-center count-cell"
            >
                <div class="d-flex justify-center">
                   <v-skeleton-loader type="text" width="30"></v-skeleton-loader>
                </div>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <!-- Local Rows -->
          <tr 
            v-for="(comparison, index) in normalizedComparisons" 
            :key="`local-${comparison.key}`"
            :data-testid="`ru-status-summary-table__row-local-${comparison.key}`"
          >
            <td 
              v-if="index === 0" 
              :rowspan="normalizedComparisons.length"
              class="scope-cell font-weight-bold"
              data-testid="ru-status-summary-table__cell-local-label"
            >
              {{ localLabel }}
            </td>
            <td class="comparison-cell" :data-testid="`ru-status-summary-table__cell-local-comparison-${comparison.key}`">
              vs {{ comparison.label }}
            </td>
            <td 
              v-for="column in tableColumns" 
              :key="`local-${comparison.key}-${column.id}`"
              class="text-center count-cell"
              :data-testid="`ru-status-summary-table__cell-local-${comparison.key}-${column.id}`"
            >
              {{ getStatusValue(column, comparison.key, 'local') }}
            </td>
          </tr>
          
          <!-- Regional Rows -->
          <tr 
            v-for="(comparison, index) in normalizedComparisons" 
            :key="`regional-${comparison.key}`"
            :data-testid="`ru-status-summary-table__row-regional-${comparison.key}`"
          >
            <td 
              v-if="index === 0" 
              :rowspan="normalizedComparisons.length"
              class="scope-cell font-weight-bold"
              data-testid="ru-status-summary-table__cell-regional-label"
            >
              {{ regionalLabel }}
            </td>
            <td class="comparison-cell" :data-testid="`ru-status-summary-table__cell-regional-comparison-${comparison.key}`">
              vs {{ comparison.label }}
            </td>
            <td 
              v-for="column in tableColumns" 
              :key="`regional-${comparison.key}-${column.id}`"
              class="text-center count-cell"
              :data-testid="`ru-status-summary-table__cell-regional-${comparison.key}-${column.id}`"
            >
              {{ getStatusValue(column, comparison.key, 'regional') }}
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </div>
</template>

<script>
/**
 * RUStatusSummaryTable Component
 * Displays RU status summary data in a table format with:
 * - Rows: Local and Regional scopes with comparison types (Proforma/Forecast)
 * - Columns: Status types (Total, Approved, Pending, etc.)
 */
const DEFAULT_COMPARISONS = Object.freeze(['proforma', 'forecast']);

export default {
  name: 'RUStatusSummaryTable',
  props: {
    /** @type {String} Label for Local scope */
    localLabel: { type: String, default: 'Local' },
    
    /** @type {String} Label for Regional scope */
    regionalLabel: { type: String, default: 'Regional' },
    
    /** @type {String} Label for the scope column header */
    scopeHeaderLabel: { type: String, default: 'Scope' },
    
    /** @type {Array} Comparison types to display */
    comparisons: {
      type: Array,
      default: () => DEFAULT_COMPARISONS.slice(),
    },
    
    /** @type {Object} Commentary summary data from API */
    commentarysummarydata: { type: Object, default: () => ({}) },

    /** @type {Boolean} Loading state */
    loading: { type: Boolean, default: false },
  },
  computed: {
    /**
     * Normalized comparison objects with keys and labels
     */
    normalizedComparisons() {
      const source = this.comparisons && this.comparisons.length
        ? this.comparisons
        : DEFAULT_COMPARISONS;

      return source.map((comparison) => {
        const key = String(comparison || '').toLowerCase();
        return {
          key,
          label: this.getComparisonLabel(key),
        };
      });
    },
    
    /**
     * Table column definitions with IDs, labels, tooltips, and CSS classes
     */
    tableColumns() {
      const notInScopeLabel = this.getNotInScopeLabel();
      return [
        { 
          id: 'count', 
          shortLabel: 'Total', 
          tooltip: 'Total GL Count', 
          headerClasses: ['font-weight-bold'], 
          usesComparison: true 
        },
        { 
          id: 'approved', 
          shortLabel: 'Approved', 
          tooltip: 'Approved GL Count', 
          headerClasses: ['success--text'], 
          usesComparison: true 
        },
        { 
          id: 'pendingapproval', 
          shortLabel: 'In Review', 
          tooltip: 'In Review GL Count (Awaiting Approval)', 
          headerClasses: ['warning--text'], 
          usesComparison: true 
        },
        { 
          id: 'pending', 
          shortLabel: 'In Submission', 
          tooltip: 'In Submission GL Count (Awaiting Submission)', 
          headerClasses: ['info--text'], 
          usesComparison: true 
        },
        { 
          id: 'rejected', 
          shortLabel: 'Rejected', 
          tooltip: 'Rejected GL Count', 
          headerClasses: ['error--text'], 
          usesComparison: true 
        },
        { 
          id: 'immaterial', 
          shortLabel: 'Immaterial Variance', 
          tooltip: 'Immaterial Variance GL Count', 
          headerClasses: ['grey--text'], 
          usesComparison: true 
        },
        { 
          id: 'nodata', 
          shortLabel: 'Nil Value Accounts', 
          tooltip: 'Nil Value Accounts GL Count', 
          headerClasses: ['grey--text'], 
          usesComparison: true, 
          fallbackToScopeOnly: true 
        },
        { 
          id: 'notinscope', 
          shortLabel: 'Not in Scope', 
          tooltip: notInScopeLabel + ' GL Count', 
          headerClasses: ['grey--text'], 
          usesComparison: true, 
          fallbackToScopeOnly: true 
        },
      ];
    },
    
    /**
     * Total number of columns for colspan in skeleton loader
     */
    totalColumnCount() {
      // Scope + Comparison + Data Columns
      return 2 + (this.tableColumns ? this.tableColumns.length : 0);
    },
  },
  methods: {
    /**
     * Get label for comparison type using Nova labels
     * @param {String} key - Comparison key (proforma/forecast)
     * @returns {String} Localized label
     */
    getComparisonLabel(key) {
      if (key === 'proforma') {
        return 'Comparative 1';
      }
      if (key === 'forecast') {
        return 'Comparative 2';
      }
      return key;
    },
    
    /**
     * Helper to get Nova label with fallback
     * @returns {String} Label value
     */
    getNovaLabel() {
      const args = Array.prototype.slice.call(arguments);
      if (this.$nova && typeof this.$nova.labelsName === 'function') {
        return this.$nova.labelsName.apply(this.$nova, args);
      }
      return args.length > 2 ? args[2] : '';
    },
    
    /**
     * Get "Not in Scope" label
     * @returns {String} Localized label
     */
    getNotInScopeLabel() {
      return this.getNovaLabel('cycle_page', 'commentary_filter_not_in_scope_label', 'Not in Scope');
    },
    
    /**
     * Get status value from commentary summary data
     * @param {Object} column - Column definition
     * @param {String} comparisonKey - Comparison type key
     * @param {String} scope - Scope type (local/regional)
     * @returns {Number} Status count value
     */
    getStatusValue(column, comparisonKey, scope) {
      const data = this.commentarysummarydata || {};
      if (!column || !column.id) return 0;

      // Total count comes directly from API as a top-level `count`
      // (not split by scope/comparison in some payloads)
      if (column.id === 'count' && data.count != null) {
        return Number(data.count) || 0;
      }

      return this.fetchValue(column, comparisonKey, scope, data);
    },

    /**
     * Internal helper to fetch value with various fallback strategies
     */
    fetchValue(column, comparisonKey, scope, data) {
      const comparison = String(comparisonKey || '').toLowerCase();
      
      // Strategy 1: Try finding value using standard patterns (exact, prefixed, case-insensitive)
      const getValue = (colId) => {
          return this.lookupValue(colId, scope, comparison, data);
      };

      // 1. Standard lookup
      let val = getValue(column.id);
      if (val > 0) return val;

      // 2. Fallback to scope-only keys (e.g. for nodata/notinscope)
      if (column.fallbackToScopeOnly) {
          // Lookup without comparison part
          // We pass empty comparison to lookupValue to simulate scope-only key (e.g. nodata_local)
          // But lookupValue constructs `${id}_${scope}_${comparison}`. 
          // So we simply construct the key manually and check raw data to avoid complexity
          const keysToCheck = [
             `${column.id}_${scope}`,
             `control_${column.id}_${scope}`,
             `management_${column.id}_${scope}`
          ];
          
          for (const k of keysToCheck) {
             const v = this.findValueCaseInsensitive(data, k);
             if (v > 0) return Number(v);
          }
      }

      return 0;
    },

    /**
     * Lookup value supporting prefixes, exact match, and case-insensitivity
     */
    lookupValue(id, scope, comparison, data) {
         // Construct base suffix: _local_proforma
         const suffix = `_${scope}_${comparison}`;
         
         // 1. Try unprefixed: approved_local_proforma
         const key1 = `${id}${suffix}`;
         const v1 = this.findValueCaseInsensitive(data, key1);
         if (v1 > 0) return Number(v1);

         // 2. Try prefixed keys and sum them: control_approved_local_proforma + management_approved_local_proforma
         // Note: Some systems might use only one of them, or both.
         const keyControl = `control_${id}${suffix}`;
         const keyMgmt = `management_${id}${suffix}`;
         
         const vControl = Number(this.findValueCaseInsensitive(data, keyControl)) || 0;
         const vMgmt = Number(this.findValueCaseInsensitive(data, keyMgmt)) || 0;
         
         const total = vControl + vMgmt;
         if (total > 0) return total;

         return 0;
    },

    /**
     * Find value in object by key, ignoring case
     */
    findValueCaseInsensitive(data, targetKey) {
        if (data[targetKey] != null) return data[targetKey];

        const targetLower = targetKey.toLowerCase();
        const foundKey = Object.keys(data).find(k => k.toLowerCase() === targetLower);
        if (foundKey) return data[foundKey];
        
        return 0;
    },
  },
};
</script>

<style scoped>
.ru-status-summary-table {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  overflow: hidden;
}

.status-table {
  width: 100%;
}

.status-table th {
  height: 120px;
  min-height: 120px;
  color: var(--text-gray-default, #2C2C35);
   
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 600;
  line-height: 1.25rem;
  text-transform: capitalize;
  white-space: normal;
  word-wrap: break-word;
  padding: 12px 8px !important;
  vertical-align: middle;
  min-width: 80px;
}

.status-table td {
  padding: 10px 8px !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

/* Count cell typography */
.count-cell {
  color: var(--text-gray-default, #2C2C35);
   
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.25rem;
  text-transform: capitalize;
}

.scope-cell {
  background-color: #fafafa;
  vertical-align: middle !important;
  border-right: 1px solid rgba(0, 0, 0, 0.08);
}

.comparison-cell {
  white-space: nowrap;
  color: #666;
}

.column-header {
  /* cursor: help; */
  /* border-bottom: 1px dashed currentColor; */
}

/* Alternate row background for better readability */
.status-table tbody tr:nth-child(odd) {
  background-color: #fafafa;
}

.status-table tbody tr:nth-child(even) {
  background-color: #ffffff;
}

/* Hover effect */
.status-table tbody tr:hover {
  background-color: #f0f0f0 !important;
}
</style>
